import React, { memo, useCallback } from 'react';
import { NodeProps, Node, useReactFlow } from '@xyflow/react';
import { LaneNodeData } from '../types';
import { useDiagramStore } from '../store/diagramStore';

/**
 * Горизонтальная зона ответственности (lane).
 * - Фоновый прямоугольник, pointer-events: none на основном слое
 * - Ручка изменения высоты внизу (pointer-events: auto — работает сквозь none)
 * - Ручка изменения ширины справа (общая для всех зон)
 */
const LaneNode: React.FC<NodeProps<Node<LaneNodeData>>> = ({ data }) => {
  const { label, color, laneId, height } = data;
  const { getZoom } = useReactFlow();
  const setLaneHeight = useDiagramStore(s => s.setLaneHeight);
  const setTotalWidth = useDiagramStore(s => s.setTotalWidth);
  const manualTotalWidth = useDiagramStore(s => s.manualTotalWidth);

  /* ── Drag: resize height ── */
  const onHeightDragStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;

    const onMove = (me: MouseEvent) => {
      const zoom = getZoom();
      const newH = Math.max(60, startH + (me.clientY - startY) / zoom);
      setLaneHeight(laneId, newH);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [height, laneId, getZoom, setLaneHeight]);

  /* ── Drag: resize total width ── */
  const onWidthDragStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    // current rendered width = offsetWidth of the lane node wrapper
    const currentW = (e.currentTarget as HTMLElement).closest('.react-flow__node')
      ?.getBoundingClientRect().width ?? (manualTotalWidth ?? 800);

    const onMove = (me: MouseEvent) => {
      const zoom = getZoom();
      const newW = Math.max(400, currentW + (me.clientX - startX) / zoom);
      setTotalWidth(newW);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [manualTotalWidth, getZoom, setTotalWidth]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: color,
        opacity: 0.35,
        borderBottom: '1px solid #ccc',
        position: 'relative',
        pointerEvents: 'none',
      }}
    >
      {/* Lane label */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, bottom: 0,
          width: 130,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: color,
          opacity: 1,
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          borderRight: '1px solid #bbb',
          zIndex: 1,
          pointerEvents: 'none',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'rotate(180deg)',
          letterSpacing: 1,
        }}
      >
        {label}
      </div>

      {/* Height resize handle — bottom edge */}
      <div
        onMouseDown={onHeightDragStart}
        title="Потяните для изменения высоты зоны"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 130,
          right: 16,
          height: 8,
          cursor: 'ns-resize',
          pointerEvents: 'auto',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{
          width: 40, height: 3, borderRadius: 2,
          background: 'rgba(0,0,0,0.18)',
          transition: 'background 0.15s',
        }} />
      </div>

      {/* Width resize handle — right edge */}
      <div
        onMouseDown={onWidthDragStart}
        title="Потяните для изменения ширины диаграммы"
        style={{
          position: 'absolute',
          top: 0,
          bottom: 8,
          right: 0,
          width: 8,
          cursor: 'ew-resize',
          pointerEvents: 'auto',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{
          width: 3, height: 40, borderRadius: 2,
          background: 'rgba(0,0,0,0.18)',
          transition: 'background 0.15s',
        }} />
      </div>
    </div>
  );
};

export default memo(LaneNode);
